// Chat activity for the signed-in user (Chat → Activity), derived from messages and read markers — nothing
// extra is stored. An item is unread while it's newer than my read marker for its channel; marking it read
// moves that marker forward, exactly like opening the channel does.
import { useMemo } from 'react';
import type { Channel, ChannelRead, Message } from '@shared/types';
import type { Op } from '@shared/sync';
import { newId } from '@shared/ids';
import { getDb, getMe, mutate, patch, put, useDb, useMe, type Tables } from '@/lib/store';
import { useWorkspaceId } from '@/lib/nav';

export type ActivityKind = 'mention' | 'dm' | 'thread' | 'reaction';

export interface ActivityItem {
  key: string;
  kind: ActivityKind;
  message: Message;
  channel: Channel;
  /** who did it: the author, or for reactions the latest reactor */
  actorId: string;
  /** everyone (but me) who reacted, for reaction items */
  reactorIds?: string[];
  at: number;
  unread: boolean;
}

const MAX_ITEMS = 300;

export const mentionsUser = (html: string, userId: string) => html.includes(`data-id="${userId}"`);

/** The newest moment covered by a reaction item: reactions carry no timestamp of their own, so use the
 *  message's last change, ignoring a change that was just my own edit. */
function reactionAt(m: Message) {
  const at = m.updatedAt ?? m.createdAt;
  return m.editedAt && at - m.editedAt < 1000 ? m.createdAt : at;
}

export function buildActivity(db: Tables, me: string, ws: string): ActivityItem[] {
  const lastRead: Record<string, number> = {};
  for (const r of Object.values(db.channelReads)) if (r.userId === me) lastRead[r.channelId] = r.lastReadAt;

  // threads I'm part of: I started them or replied in them
  const myThreads = new Set<string>();
  for (const m of Object.values(db.messages)) {
    if (m.authorId !== me) continue;
    if (m.parentId) myThreads.add(m.parentId);
    else myThreads.add(m.id);
  }

  const items: ActivityItem[] = [];
  for (const m of Object.values(db.messages)) {
    const channel = db.channels[m.channelId];
    if (!channel || channel.workspaceId !== ws) continue;
    const member = channel.memberIds.includes(me);
    if (channel.private && !member) continue;
    const unreadAfter = lastRead[m.channelId] ?? 0;

    if (m.authorId === me) {
      const reactorIds = [...new Set(Object.values(m.reactions ?? {}).flat())].filter((u) => u !== me);
      if (!reactorIds.length) continue;
      const at = reactionAt(m);
      items.push({ key: `reaction:${m.id}`, kind: 'reaction', message: m, channel, actorId: reactorIds[reactorIds.length - 1], reactorIds, at, unread: at > unreadAfter });
      continue;
    }

    let kind: ActivityKind | null = null;
    if (mentionsUser(m.body, me)) kind = 'mention';
    else if (m.parentId && myThreads.has(m.parentId)) kind = 'thread';
    else if (channel.kind === 'dm' && member && !m.parentId) kind = 'dm';
    if (!kind) continue;
    items.push({ key: `${kind}:${m.id}`, kind, message: m, channel, actorId: m.authorId, at: m.createdAt, unread: m.createdAt > unreadAfter });
  }
  items.sort((a, b) => b.at - a.at);
  return items.slice(0, MAX_ITEMS);
}

export function useActivity(): ActivityItem[] {
  const me = useMe();
  const ws = useWorkspaceId();
  const messages = useDb((db) => db.messages);
  const channels = useDb((db) => db.channels);
  const channelReads = useDb((db) => db.channelReads);
  return useMemo(() => buildActivity(getDb(), me, ws), [messages, channels, channelReads, me, ws]);
}

/** Unread activity count for the sidebar badge. */
export function useActivityUnread(): number {
  return useActivity().filter((i) => i.unread).length;
}

/** Mark items read by moving each channel's read marker up to the newest of them. */
export function markActivityRead(items: ActivityItem[]) {
  const db = getDb();
  const me = getMe();
  const upTo: Record<string, number> = {};
  for (const i of items) upTo[i.channel.id] = Math.max(upTo[i.channel.id] ?? 0, i.at);
  const records: Record<string, ChannelRead> = {};
  for (const r of Object.values(db.channelReads)) if (r.userId === me) records[r.channelId] = r;
  const ops: Op[] = [];
  for (const [channelId, at] of Object.entries(upTo)) {
    const rec = records[channelId];
    if (rec && rec.lastReadAt >= at) continue;
    ops.push(rec ? patch('channelReads', rec.id, { lastReadAt: at }) : put('channelReads', { id: newId(), userId: me, channelId, lastReadAt: at, createdAt: Date.now() }));
  }
  if (ops.length) mutate(ops);
}
